/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import type { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry } from 'ol/geom';
import type { StyleLike } from 'ol/style/Style';
import type Style from 'ol/style/Style';
import { useRef } from 'react';
import { FeatureLayer } from './FeatureLayer';
import {
  makeLegendFromColorMap,
  colorMaps,
  makeFeatureStyleDisc,
} from '../../../edr-layer-api';
import { useProjection } from '../utils/projections';
import type { BoxSelect, ClickOnFeature, HoverSelect } from '../utils/types';

const defaultLegend = makeLegendFromColorMap({
  colors: colorMaps.temperatureLegendColorsWoW,
  min: -18,
  max: 38,
});

// Style needs to be optional to match the other layers. Therefore a default style is made here once.
const defaultStyle = (feature: FeatureLike): Style =>
  makeFeatureStyleDisc(feature, defaultLegend);

export interface OpenLayersFeatureLayerProps {
  featureCollection?: FeatureCollection;
  style?: StyleLike;
  zIndex?: number;
  hoverSelect?: HoverSelect;
  clickOnFeature?: ClickOnFeature;
  boxSelect?: BoxSelect;
  layerId?: string;
  testId?: string;
  opacity?: number;
  declutter?: boolean;
  visible?: boolean;
}

export const OpenLayersFeatureLayer: React.FC<OpenLayersFeatureLayerProps> = ({
  featureCollection,
  style = defaultStyle,
  zIndex,
  hoverSelect,
  clickOnFeature,
  boxSelect,
  layerId,
  testId,
  opacity,
  declutter = false,
  visible = true,
}: OpenLayersFeatureLayerProps) => {
  const projection = useProjection();

  const featureCollectionRef = useRef<FeatureCollection | undefined>(undefined);
  const projectionRef = useRef(projection);
  const features = useRef<Feature<Geometry>[]>([]);

  const featureCollectionHasChanges =
    featureCollectionRef.current !== featureCollection ||
    projectionRef.current !== projection;

  if (featureCollectionHasChanges) {
    if (projection && featureCollection) {
      featureCollectionRef.current = featureCollection;
      projectionRef.current = projection;
      const formatter = new GeoJSON({ featureProjection: projection });
      features.current = formatter.readFeatures(featureCollection);
    }
  }

  // If no featureCollection is given, e.g. in edit mode of a polygon, the feature should not be displayed.
  if (!featureCollection) {
    return null;
  }

  return (
    <FeatureLayer
      boxSelect={boxSelect}
      clickOnFeature={clickOnFeature}
      declutter={declutter}
      features={features.current}
      hoverSelect={hoverSelect}
      layerId={layerId}
      opacity={opacity}
      style={style}
      testId={testId}
      visible={visible}
      zIndex={zIndex}
    />
  );
};
